import React, { useEffect, useState } from 'react';
import { UserIcon, ShieldCheckIcon, FileTextIcon, CameraIcon, CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, AlertCircleIcon, LoaderIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import VerificationStep from '../components/KYC/VerificationStep';
import DocumentUpload from '../components/KYC/DocumentUpload';
import { kycService } from '../services/kycService';
import { KycFormState, KycStatus, KycError } from '../types/kyc';
import { handleApiError } from '../utils/api';
const KycVerification = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idTypes, setIdTypes] = useState<string[]>([]);
  const [addressDocTypes, setAddressDocTypes] = useState<string[]>([]);
  // Form state
  const [formState, setFormState] = useState<KycFormState>({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      nationality: 'Nigerian'
    },
    idVerification: {
      idType: '',
      frontImage: null,
      backImage: null
    },
    addressVerification: {
      documentType: '',
      addressDocument: null
    },
    consent: false
  });
  // Fetch country requirements when nationality changes
  useEffect(() => {
    const fetchRequirements = async () => {
      if (formState.personalInfo.nationality) {
        try {
          // Convert nationality to country code (simplified)
          const countryCode = formState.personalInfo.nationality === 'Nigerian' ? 'NG' : 'US';
          const requirements = await kycService.getCountryRequirements(countryCode);
          setIdTypes(requirements.idTypes);
          setAddressDocTypes(requirements.addressDocumentTypes);
        } catch (err) {
          console.error('Failed to fetch country requirements:', err);
          // Use default values if API fails
          setIdTypes(['Passport', "Driver's License", 'National ID Card', 'Residence Permit']);
          setAddressDocTypes(['Utility Bill (Water, Electric, Gas)', 'Bank Statement', 'Tax Statement', 'Rental Agreement', 'Insurance Statement']);
        }
      }
    };
    fetchRequirements();
  }, [formState.personalInfo.nationality]);
  // Handle form input changes
  const handleInputChange = (step: 'personalInfo' | 'idVerification' | 'addressVerification', field: string, value: string) => {
    setFormState(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [field]: value
      }
    }));
  };
  // Handle file uploads
  const handleFileChange = (step: 'idVerification' | 'addressVerification', field: string, file: File | null) => {
    setFormState(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [field]: file
      }
    }));
  };
  // Handle checkbox change
  const handleConsentChange = (checked: boolean) => {
    setFormState(prev => ({
      ...prev,
      consent: checked
    }));
  };
  // Validate current step
  const validateCurrentStep = (): boolean => {
    setError(null);
    switch (currentStep) {
      case 1:
        {
          const {
            firstName,
            lastName,
            email,
            phone,
            dateOfBirth,
            nationality
          } = formState.personalInfo;
          if (!firstName || !lastName || !email || !phone || !dateOfBirth || !nationality) {
            setError('Please fill in all required fields');
            return false;
          }
          // Basic email validation
          if (!/^\S+@\S+\.\S+$/.test(email)) {
            setError('Please enter a valid email address');
            return false;
          }
          return true;
        }
      case 2:
        {
          const {
            idType,
            frontImage,
            backImage
          } = formState.idVerification;
          if (!idType) {
            setError('Please select an ID type');
            return false;
          }
          if (!frontImage) {
            setError('Please upload the front of your ID');
            return false;
          }
          if (!backImage && idType !== 'Passport') {
            setError('Please upload the back of your ID');
            return false;
          }
          return true;
        }
      case 3:
        {
          const {
            documentType,
            addressDocument
          } = formState.addressVerification;
          if (!documentType) {
            setError('Please select a document type');
            return false;
          }
          if (!addressDocument) {
            setError('Please upload your proof of address');
            return false;
          }
          return true;
        }
      case 4:
        {
          if (!formState.consent) {
            setError('Please consent to the processing of your data');
            return false;
          }
          return true;
        }
      default:
        return true;
    }
  };
  // Submit current step
  const submitCurrentStep = async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      switch (currentStep) {
        case 1:
          {
            const response = await kycService.submitPersonalInfo(formState.personalInfo);
            setFormState(prev => ({
              ...prev,
              stepId: response.stepId
            }));
            return response.success;
          }
        case 2:
          {
            if (!formState.stepId) throw new Error('Missing step ID');
            const response = await kycService.submitIdVerification({
              stepId: formState.stepId,
              idType: formState.idVerification.idType,
              frontImage: formState.idVerification.frontImage!,
              backImage: formState.idVerification.backImage || formState.idVerification.frontImage!
            });
            return response.success;
          }
        case 3:
          {
            if (!formState.stepId) throw new Error('Missing step ID');
            const response = await kycService.submitAddressVerification({
              stepId: formState.stepId,
              documentType: formState.addressVerification.documentType,
              addressDocument: formState.addressVerification.addressDocument!
            });
            return response.success;
          }
        case 4:
          {
            if (!formState.stepId) throw new Error('Missing step ID');
            const response = await kycService.submitFinalVerification({
              stepId: formState.stepId,
              consent: formState.consent
            });
            return response.success;
          }
        default:
          return false;
      }
    } catch (err) {
      setError(handleApiError(err));
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  const handleNextStep = async () => {
    if (!validateCurrentStep()) {
      return;
    }
    const success = await submitCurrentStep();
    if (success) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        // Complete verification process
        setVerificationComplete(true);
      }
    }
  };
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-1281">
      <div className="container mx-auto px-4 py-8" data-id="element-1282">
        <div className="max-w-3xl mx-auto" data-id="element-1283">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2" data-id="element-1284">
            Identity Verification
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-1285">
            Complete your KYC verification to ensure secure property
            transactions on DiasporaHomes
          </p>
          {/* Progress Steps */}
          <div className="mb-8" data-id="element-1286">
            <div className="flex items-center justify-between" data-id="element-1287">
              {[1, 2, 3, 4].map(step => <div key={step} className="flex flex-col items-center relative flex-1" data-id="element-1288">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step <= currentStep ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-1289">
                    {step < currentStep ? <CheckCircleIcon size={20} data-id="element-1290" /> : step}
                  </div>
                  <div className="text-xs mt-2 text-center" data-id="element-1291">
                    {step === 1 ? 'Personal Info' : step === 2 ? 'ID Verification' : step === 3 ? 'Proof of Address' : 'Review'}
                  </div>
                  {step < 4 && <div className={`absolute top-5 left-1/2 w-full h-0.5 ${step < currentStep ? 'bg-green-600' : 'bg-gray-200'}`} data-id="element-1292"></div>}
                </div>)}
            </div>
          </div>
          {/* Error Message */}
          {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6" data-id="element-1293">
              <div className="flex" data-id="element-1294">
                <AlertCircleIcon className="h-5 w-5 text-red-600 mr-2" data-id="element-1295" />
                <span data-id="element-1296">{error}</span>
              </div>
            </div>}
          {/* Verification Steps */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-1297">
            {!verificationComplete ? <>
                {currentStep === 1 && <VerificationStep title="Personal Information" description="Please provide your basic personal information for verification" icon={<UserIcon className="h-8 w-8 text-green-600" data-id="element-1299" />} data-id="element-1298">
                    <div className="space-y-4 mt-6" data-id="element-1300">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1301">
                        <div data-id="element-1302">
                          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1303">
                            First Name <span className="text-red-500" data-id="element-1304">*</span>
                          </label>
                          <input type="text" id="firstName" value={formState.personalInfo.firstName} onChange={e => handleInputChange('personalInfo', 'firstName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-1305" />
                        </div>
                        <div data-id="element-1306">
                          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1307">
                            Last Name <span className="text-red-500" data-id="element-1308">*</span>
                          </label>
                          <input type="text" id="lastName" value={formState.personalInfo.lastName} onChange={e => handleInputChange('personalInfo', 'lastName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-1309" />
                        </div>
                      </div>
                      <div data-id="element-1310">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1311">
                          Email Address <span className="text-red-500" data-id="element-1312">*</span>
                        </label>
                        <input type="email" id="email" value={formState.personalInfo.email} onChange={e => handleInputChange('personalInfo', 'email', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-1313" />
                      </div>
                      <div data-id="element-1314">
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1315">
                          Phone Number <span className="text-red-500" data-id="element-1316">*</span>
                        </label>
                        <input type="tel" id="phone" value={formState.personalInfo.phone} onChange={e => handleInputChange('personalInfo', 'phone', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-1317" />
                      </div>
                      <div data-id="element-1318">
                        <label htmlFor="dob" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1319">
                          Date of Birth <span className="text-red-500" data-id="element-1320">*</span>
                        </label>
                        <input type="date" id="dob" value={formState.personalInfo.dateOfBirth} onChange={e => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-1321" />
                      </div>
                      <div data-id="element-1322">
                        <label htmlFor="nationality" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1323">
                          Nationality <span className="text-red-500" data-id="element-1324">*</span>
                        </label>
                        <select id="nationality" value={formState.personalInfo.nationality} onChange={e => handleInputChange('personalInfo', 'nationality', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-1325">
                          <option value="" data-id="element-1326">Select your nationality</option>
                          <option value="Nigerian" data-id="element-1327">Nigerian</option>
                          <option value="American" data-id="element-1328">American</option>
                          <option value="British" data-id="element-1329">British</option>
                          <option value="Canadian" data-id="element-1330">Canadian</option>
                          <option value="Other" data-id="element-1331">Other</option>
                        </select>
                      </div>
                    </div>
                  </VerificationStep>}
                {currentStep === 2 && <VerificationStep title="ID Verification" description="Upload a valid government-issued photo ID" icon={<ShieldCheckIcon className="h-8 w-8 text-green-600" data-id="element-1333" />} data-id="element-1332">
                    <div className="space-y-6 mt-6" data-id="element-1334">
                      <div data-id="element-1335">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1336">
                          ID Type <span className="text-red-500" data-id="element-1337">*</span>
                        </label>
                        <select value={formState.idVerification.idType} onChange={e => handleInputChange('idVerification', 'idType', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-1338">
                          <option value="" data-id="element-1339">Select ID type</option>
                          {idTypes.map((type, index) => <option key={index} value={type} data-id="element-1340">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <DocumentUpload title="Front of ID" description="Upload a clear photo of the front of your ID" icon={<CameraIcon size={24} className="text-green-600" data-id="element-1342" />} onFileChange={file => handleFileChange('idVerification', 'frontImage', file)} currentFile={formState.idVerification.frontImage} data-id="element-1341" />
                      {formState.idVerification.idType !== 'Passport' && <DocumentUpload title="Back of ID" description="Upload a clear photo of the back of your ID" icon={<CameraIcon size={24} className="text-green-600" data-id="element-1344" />} onFileChange={file => handleFileChange('idVerification', 'backImage', file)} currentFile={formState.idVerification.backImage} data-id="element-1343" />}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1345">
                        <div className="flex items-start" data-id="element-1346">
                          <AlertCircleIcon size={20} className="text-blue-600 mr-3 mt-0.5" data-id="element-1347" />
                          <div data-id="element-1348">
                            <h4 className="text-blue-800 font-medium" data-id="element-1349">
                              Requirements for ID photos:
                            </h4>
                            <ul className="text-blue-800 text-sm mt-1 list-disc pl-5" data-id="element-1350">
                              <li data-id="element-1351">All four corners must be visible</li>
                              <li data-id="element-1352">The ID must not be expired</li>
                              <li data-id="element-1353">Information must be clearly legible</li>
                              <li data-id="element-1354">No glare or reflections on the ID</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </VerificationStep>}
                {currentStep === 3 && <VerificationStep title="Proof of Address" description="Upload a document that verifies your current address" icon={<FileTextIcon className="h-8 w-8 text-green-600" data-id="element-1356" />} data-id="element-1355">
                    <div className="space-y-6 mt-6" data-id="element-1357">
                      <div data-id="element-1358">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1359">
                          Document Type <span className="text-red-500" data-id="element-1360">*</span>
                        </label>
                        <select value={formState.addressVerification.documentType} onChange={e => handleInputChange('addressVerification', 'documentType', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" data-id="element-1361">
                          <option value="" data-id="element-1362">Select document type</option>
                          {addressDocTypes.map((type, index) => <option key={index} value={type} data-id="element-1363">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <DocumentUpload title="Address Document" description="Upload a clear photo of your proof of address (less than 3 months old)" icon={<FileTextIcon size={24} className="text-green-600" data-id="element-1365" />} onFileChange={file => handleFileChange('addressVerification', 'addressDocument', file)} currentFile={formState.addressVerification.addressDocument} data-id="element-1364" />
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1366">
                        <div className="flex items-start" data-id="element-1367">
                          <AlertCircleIcon size={20} className="text-blue-600 mr-3 mt-0.5" data-id="element-1368" />
                          <div data-id="element-1369">
                            <h4 className="text-blue-800 font-medium" data-id="element-1370">
                              Requirements for proof of address:
                            </h4>
                            <ul className="text-blue-800 text-sm mt-1 list-disc pl-5" data-id="element-1371">
                              <li data-id="element-1372">Document must be less than 3 months old</li>
                              <li data-id="element-1373">
                                Must show your full name and current address
                              </li>
                              <li data-id="element-1374">
                                Must be issued by a recognized organization
                              </li>
                              <li data-id="element-1375">
                                Digital bills are acceptable if they show the
                                complete information
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </VerificationStep>}
                {currentStep === 4 && <VerificationStep title="Review & Submit" description="Review your information before final submission" icon={<CheckCircleIcon className="h-8 w-8 text-green-600" data-id="element-1377" />} data-id="element-1376">
                    <div className="space-y-6 mt-6" data-id="element-1378">
                      <div className="border border-gray-200 rounded-lg" data-id="element-1379">
                        <div className="border-b border-gray-200 p-4" data-id="element-1380">
                          <h3 className="font-medium" data-id="element-1381">Personal Information</h3>
                        </div>
                        <div className="p-4 space-y-2" data-id="element-1382">
                          <div className="flex justify-between" data-id="element-1383">
                            <span className="text-gray-600" data-id="element-1384">Full Name:</span>
                            <span className="font-medium" data-id="element-1385">
                              {formState.personalInfo.firstName}{' '}
                              {formState.personalInfo.lastName}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-1386">
                            <span className="text-gray-600" data-id="element-1387">Email:</span>
                            <span className="font-medium" data-id="element-1388">
                              {formState.personalInfo.email}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-1389">
                            <span className="text-gray-600" data-id="element-1390">Phone:</span>
                            <span className="font-medium" data-id="element-1391">
                              {formState.personalInfo.phone}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-1392">
                            <span className="text-gray-600" data-id="element-1393">
                              Date of Birth:
                            </span>
                            <span className="font-medium" data-id="element-1394">
                              {formState.personalInfo.dateOfBirth}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-1395">
                            <span className="text-gray-600" data-id="element-1396">Nationality:</span>
                            <span className="font-medium" data-id="element-1397">
                              {formState.personalInfo.nationality}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="border border-gray-200 rounded-lg" data-id="element-1398">
                        <div className="border-b border-gray-200 p-4" data-id="element-1399">
                          <h3 className="font-medium" data-id="element-1400">Uploaded Documents</h3>
                        </div>
                        <div className="p-4 space-y-4" data-id="element-1401">
                          <div className="flex items-center justify-between" data-id="element-1402">
                            <div className="flex items-center" data-id="element-1403">
                              <div className="bg-green-100 p-2 rounded-lg mr-3" data-id="element-1404">
                                <ShieldCheckIcon size={20} className="text-green-600" data-id="element-1405" />
                              </div>
                              <div data-id="element-1406">
                                <p className="font-medium" data-id="element-1407">
                                  {formState.idVerification.idType}
                                </p>
                                <p className="text-sm text-gray-600" data-id="element-1408">
                                  {formState.idVerification.idType === 'Passport' ? 'Passport uploaded' : 'Front and back uploaded'}
                                </p>
                              </div>
                            </div>
                            <button className="text-blue-600 text-sm hover:underline" data-id="element-1409">
                              View
                            </button>
                          </div>
                          <div className="flex items-center justify-between" data-id="element-1410">
                            <div className="flex items-center" data-id="element-1411">
                              <div className="bg-green-100 p-2 rounded-lg mr-3" data-id="element-1412">
                                <FileTextIcon size={20} className="text-green-600" data-id="element-1413" />
                              </div>
                              <div data-id="element-1414">
                                <p className="font-medium" data-id="element-1415">
                                  {formState.addressVerification.documentType}
                                </p>
                                <p className="text-sm text-gray-600" data-id="element-1416">
                                  Proof of address
                                </p>
                              </div>
                            </div>
                            <button className="text-blue-600 text-sm hover:underline" data-id="element-1417">
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-1418">
                        <div className="flex items-start" data-id="element-1419">
                          <AlertCircleIcon size={20} className="text-yellow-600 mr-3 mt-0.5" data-id="element-1420" />
                          <div data-id="element-1421">
                            <p className="text-yellow-800" data-id="element-1422">
                              By submitting your verification, you confirm that
                              all information provided is accurate and
                              authentic. Submitting false information may result
                              in account termination.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center" data-id="element-1423">
                        <input type="checkbox" id="consent" checked={formState.consent} onChange={e => handleConsentChange(e.target.checked)} className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" data-id="element-1424" />
                        <label htmlFor="consent" className="ml-2 block text-sm text-gray-700" data-id="element-1425">
                          I consent to DiasporaHomes processing my personal data
                          for verification purposes
                        </label>
                      </div>
                    </div>
                  </VerificationStep>}
              </> : <div className="text-center py-8" data-id="element-1426">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-1427">
                  <CheckCircleIcon size={32} className="text-green-600" data-id="element-1428" />
                </div>
                <h2 className="text-2xl font-bold mb-2" data-id="element-1429">
                  Verification Submitted!
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-1430">
                  Your identity verification has been submitted successfully.
                  Our team will review your documents and update your account
                  status within 24-48 hours.
                </p>
                <Link to="/dashboard" className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition" data-id="element-1431">
                  Go to Dashboard
                </Link>
              </div>}
          </div>
          {/* Navigation Buttons */}
          {!verificationComplete && <div className="flex justify-between" data-id="element-1432">
              <button onClick={handlePrevStep} className={`flex items-center px-4 py-2 rounded-lg ${currentStep === 1 || isLoading ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} disabled={currentStep === 1 || isLoading} data-id="element-1433">
                <ArrowLeftIcon size={16} className="mr-2" data-id="element-1434" />
                Previous
              </button>
              <button onClick={handleNextStep} disabled={isLoading} className={`flex items-center bg-green-600 text-white px-6 py-2 rounded-lg ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-green-700'} transition`} data-id="element-1435">
                {isLoading ? <>
                    <LoaderIcon size={16} className="animate-spin mr-2" data-id="element-1436" />
                    Processing...
                  </> : <>
                    {currentStep === 4 ? 'Submit Verification' : 'Next'}
                    {currentStep !== 4 && <ArrowRightIcon size={16} className="ml-2" data-id="element-1437" />}
                  </>}
              </button>
            </div>}
        </div>
      </div>
    </div>;
};
export default KycVerification;